import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import type { AuthConfig, HydratedAuthSession } from './types';
import { loadAuthSession } from './session';

/**
 * Build a singleton-style OIDC user manager. Caller owns lifecycle and is
 * responsible for `signinRedirectCallback` after the IdP returns.
 */
export function createUserManager(config: AuthConfig): UserManager {
  const settings: ConstructorParameters<typeof UserManager>[0] = {
    authority: config.authority,
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.postLogoutRedirectUri ?? config.redirectUri,
    silent_redirect_uri: config.silentRedirectUri ?? `${config.redirectUri}#silent-callback`,
    response_type: 'code',
    scope: (config.scopes ?? ['openid', 'profile', 'email']).join(' '),
    automaticSilentRenew: config.automaticSilentRenew ?? true,
    loadUserInfo: true,
  };
  if (config.metadata) {
    settings.metadata = config.metadata;
  }
  if (typeof window !== 'undefined') {
    settings.userStore = new WebStorageStateStore({ store: window.sessionStorage });
  }
  return new UserManager(settings);
}

export function isOidcUserExpired(user: User): boolean {
  return user.expires_at === undefined || user.expires_at <= Math.floor(Date.now() / 1000);
}

/**
 * Convert a fresh OIDC user to a server-hydrated CGuard session. This now
 * loads `/v1/identity/me`; callers must await it.
 */
export async function toSession(user: User | null): Promise<HydratedAuthSession | null> {
  if (!user) return null;
  if (isOidcUserExpired(user)) return null;
  return loadAuthSession(user);
}

/**
 * Read the ordinary app session from its OIDC user store without changing the
 * active browser session. Dedicated elevated-token callbacks use this to bind
 * the helper ceremony to the actor who started it.
 */
export async function readStoredAuthSession(
  config: AuthConfig,
): Promise<HydratedAuthSession | null> {
  return await toSession(await createUserManager(config).getUser());
}
